import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useGameStore } from '@/store/gameStore';
import { Crown, Skull, Handshake, Flag, Play, Save, X, Share2 } from 'lucide-react';
import { updateStats, saveGame } from '@/utils/storage';
import ShareDialog from './ShareDialog';

const GameOverDialog = ({ onNewGame }) => {
  const { 
    gameStatus, 
    winner, 
    playerColor, 
    history, 
    blunderCount, 
    boardTheme,
    position,
    aiDifficulty,
    moveAnalysis,
    enterReviewMode
  } = useGameStore();
  
  const statsUpdated = useRef(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saved, setSaved] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  const isGameOver = ['checkmate', 'stalemate', 'draw', 'resigned'].includes(gameStatus);
  const playerWon = winner === playerColor;
  const isDraw = gameStatus === 'stalemate' || gameStatus === 'draw';
  
  // Reset dismissed state when new game ends
  useEffect(() => {
    if (isGameOver) {
      setDismissed(false);
      setSaved(false);
      setShowSaveInput(false);
      setSaveName('');
    }
  }, [isGameOver, position]);
  
  // Update stats when game ends
  useEffect(() => {
    if (isGameOver && !statsUpdated.current) {
      const result = isDraw ? 'draw' : playerWon ? 'win' : 'loss';
      updateStats(result, history.length, blunderCount);
      statsUpdated.current = true;
    }
    
    // Reset flag when game restarts
    if (!isGameOver) {
      statsUpdated.current = false;
    }
  }, [isGameOver, isDraw, playerWon, history.length, blunderCount]);
  
  if (!isGameOver || dismissed) return null;
  
  const getTitle = () => {
    if (gameStatus === 'checkmate') {
      return playerWon ? 'Victory!' : 'Defeat';
    }
    if (gameStatus === 'stalemate') return 'Stalemate';
    if (gameStatus === 'draw') return 'Draw';
    if (gameStatus === 'resigned') {
      return playerWon ? 'Opponent Resigned' : 'You Resigned';
    }
    return 'Game Over';
  };
  
  const getIcon = () => {
    if (playerWon) return <Crown className="w-16 h-16 text-yellow-400" />;
    if (isDraw) return <Handshake className="w-16 h-16 text-gray-400" />;
    if (gameStatus === 'resigned') return <Flag className="w-16 h-16 text-red-400" />;
    return <Skull className="w-16 h-16 text-red-500" />;
  };
  
  const getMessage = () => {
    if (gameStatus === 'checkmate' && playerWon) {
      return 'Excellent play! You checkmated your opponent.';
    }
    if (gameStatus === 'checkmate' && !playerWon) {
      return 'The AI found checkmate. Better luck next time!';
    }
    if (gameStatus === 'stalemate') {
      return 'The game ended in stalemate - no legal moves available.';
    }
    if (gameStatus === 'draw') {
      return 'The game ended in a draw.';
    }
    if (gameStatus === 'resigned') {
      return playerWon ? 'Your opponent has resigned.' : 'You resigned the game.';
    }
    return '';
  };
  
  const handleReviewGame = () => {
    setDismissed(true);
    enterReviewMode();
  };
  
  const handleSaveGame = () => {
    if (saveName.trim()) {
      const gameData = {
        fen: position,
        history,
        playerColor,
        aiDifficulty,
        moveAnalysis,
        result: isDraw ? 'draw' : playerWon ? 'win' : 'loss',
      };
      
      saveGame(saveName.trim(), gameData);
      setSaved(true);
      setShowSaveInput(false);
    }
  };
  
  const handleDismiss = () => {
    setDismissed(true);
  };
  
  return (
    <Dialog open={isGameOver && !dismissed} onOpenChange={() => {}}>
      <DialogContent 
        className={`
          ${boardTheme === 'cosmic'
            ? 'bg-slate-900 border-purple-500/30 text-white'
            : 'bg-amber-50 border-amber-300 text-gray-900'
          }
        `}
        data-testid="game-over-dialog"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className={`
            absolute top-3 right-3 p-1 rounded-lg transition-colors
            ${boardTheme === 'cosmic' 
              ? 'text-gray-500 hover:text-white hover:bg-slate-800' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }
          `}
        >
          <X className="w-5 h-5" />
        </button>
        
        <DialogHeader>
          <DialogTitle className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              {getIcon()}
              <span className={`
                text-3xl font-bold
                ${playerWon 
                  ? 'text-yellow-400' 
                  : isDraw 
                    ? 'text-gray-400' 
                    : 'text-red-400'
                }
              `}>
                {getTitle()}
              </span>
            </motion.div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="text-center py-4">
          <p className={`${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-600'}`}>
            {getMessage()}
          </p>
          
          <div className={`
            mt-4 p-4 rounded-lg
            ${boardTheme === 'cosmic' ? 'bg-slate-800' : 'bg-amber-100'}
          `}>
            <div className="flex justify-around text-sm">
              <div>
                <div className={boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}>Moves</div>
                <div className="font-bold text-lg">{history.length}</div>
              </div>
              <div>
                <div className={boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}>Blunders</div>
                <div className={`font-bold text-lg ${blunderCount > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {blunderCount}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Save Game Section */}
        {showSaveInput ? (
          <div className={`
            p-3 rounded-lg mb-3
            ${boardTheme === 'cosmic' ? 'bg-slate-800' : 'bg-amber-100'}
          `}>
            <input
              type="text"
              placeholder="Enter a name for this game..."
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className={`
                w-full px-3 py-2 rounded-lg mb-2
                ${boardTheme === 'cosmic'
                  ? 'bg-slate-700 border border-slate-600 text-white placeholder-gray-500'
                  : 'bg-white border border-amber-200 text-gray-900 placeholder-gray-400'
                }
              `}
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                onClick={handleSaveGame}
                disabled={!saveName.trim()}
                className={`
                  flex-1
                  ${boardTheme === 'cosmic'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                  }
                `}
              >
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSaveInput(false)}
                className={boardTheme === 'cosmic' ? 'border-slate-600' : ''}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : saved ? (
          <div className={`
            p-3 rounded-lg mb-3 text-center
            ${boardTheme === 'cosmic' ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}
          `}>
            ✓ Game saved!
          </div>
        ) : null}
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              onClick={handleReviewGame}
              variant="outline"
              className={`
                flex-1
                ${boardTheme === 'cosmic'
                  ? 'border-purple-500/50 text-purple-300 hover:bg-purple-900/30'
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50'
                }
              `}
              data-testid="review-game-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Review Game
            </Button>
            
            <Button
              onClick={() => setShowShareDialog(true)}
              variant="outline"
              className={`
                flex-1
                ${boardTheme === 'cosmic'
                  ? 'border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/30'
                  : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                }
              `}
              data-testid="share-game-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          
          {!saved && !showSaveInput && (
            <Button
              onClick={() => setShowSaveInput(true)}
              variant="outline"
              className={`
                w-full
                ${boardTheme === 'cosmic'
                  ? 'border-green-500/50 text-green-300 hover:bg-green-900/30'
                  : 'border-green-300 text-green-700 hover:bg-green-50'
                }
              `}
              data-testid="save-game-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Game
            </Button>
          )}
          
          <Button
            onClick={onNewGame}
            className={`
              w-full
              ${boardTheme === 'cosmic'
                ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }
            `}
            data-testid="new-game-after-dialog"
          >
            New Game
          </Button>
        </div>
        
        {/* Share Dialog */}
        <ShareDialog 
          isOpen={showShareDialog} 
          onClose={() => setShowShareDialog(false)} 
        />
      </DialogContent>
    </Dialog>
  );
};

export default GameOverDialog;
